import React, { useEffect, useRef, useState } from 'react';
import styles from './MabelSidebar.module.scss';
import { useIsFetching } from 'react-query';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import logo from 'assets/icons/Mabel_Icon_BlueGradient.png';
import SvgIcon from 'shared/components/SvgIcon/SvgIcon';
import highPriority from 'assets/icons/HighPriorityIcon.svg';
import lowPriority from 'assets/icons/LowPriorityIcon.svg';
import mediumPriority from 'assets/icons/MediumPriorityIcon.svg';
import { ChatBubble } from '@mui/icons-material';
import setting from '../../../assets/new_icons/NewSettings.svg';
import newbell from '../../../assets/new_icons/NewBell.svg';
import newbellOutline from '../../../assets/new_icons/NewBellOutline.svg';
import drawer from '../../../assets/new_icons/NewDrawer.svg';
import search from '../../../assets/new_icons/NewSearch.svg';
import { useLocation } from 'react-router-dom';
import MabelScoreCardIcon from 'assets/svgComponent/IconComponent';
import TodoComponent from 'assets/svgComponent/TodoComponent';
import IconSchedule from 'assets/svgComponent/IconSchedule';
import LogbookComponent from 'assets/svgComponent/LogbookComponent';
import OnboardingComponent from 'assets/svgComponent/OnboardingComponent';
import GuestConnect from 'assets/svgComponent/GuestConnect';
import GameMasterComponent from 'assets/svgComponent/GameMasterComponent';
import DashBoardComponent from 'assets/svgComponent/DashBoardComponent';
import EventCalenderComponent from 'assets/svgComponent/EventCalenderComponent';
import BroadCastComponent from 'assets/svgComponent/BroadCastComponent';
import CheckListComponent from 'assets/svgComponent/CheckListComponent';
import { useWorkplaceUser } from '../../../module/documents/utils';
import { CircularProgress } from '@mui/material';
import { Notifications } from 'module/notifications';
import { useQuery } from 'react-query';
import { fetchUserNotifications } from 'module/notifications/api/notificationsApi';
import { mapAPINotificationsToNotifications } from 'module/notifications/utils/notificationMappers';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import moment from 'moment';

interface MabelSidebarProps {
	activeItem?: string;
	// onItemClick?: (itemName: string) => void;
	hoveredLayout?: boolean;
	setHoveredLayout?: (value: boolean) => void;
	onCollapseChange?: (collapsed: boolean) => void;
}

const MabelSidebar: React.FC<MabelSidebarProps> = ({ setHoveredLayout, onCollapseChange }) => {
	const [collapsed, setCollapsed] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [manualToggle, setManualToggle] = useState(false);
	const [isExpanded, setIsExpanded] = useState(true);
	// const { open, setOpen, handleOpen } = useDocumentContext();
	const { userOrg } = useWorkplaceUser();
	const products = userOrg?.products || [];
	const isProductAvailable = (productName: string) => {
		return products.some((product) => product.label === productName);
	};

	// Permission checks for modules
	const { hasModuleViewPermission, hasViewPermission } = usePermissionCheck();

	// Check module permissions
	// For modules with "My" and "All" permissions, show if user has at least "My" view permission
	const canViewChecklist = hasModuleViewPermission('Checklist - My') || hasModuleViewPermission('Checklist - All');
	const canViewToDo = hasModuleViewPermission('To-Do - My') || hasModuleViewPermission('To-Do - All');
	const canViewLogbook = hasModuleViewPermission('Logbook');
	const canViewDashboard = hasModuleViewPermission('Dashboard');
	const canViewGuestConnect = hasModuleViewPermission('Guest Connect');
	const canViewOnboarding = hasModuleViewPermission('Onboarding');
	const canViewScorecard = hasModuleViewPermission('Scorecard');
	
	// For modules using hasViewPermission
	const canViewEvents = hasModuleViewPermission('Events');
	const canViewSchedule = hasViewPermission('Schedule - My') || hasViewPermission('Schedule - All');
	const canViewBroadcast =
		hasModuleViewPermission('Broadcast - My') ||
		hasModuleViewPermission('Broadcast - All') ||
		hasModuleViewPermission('Broadcast - Organization');
	const canViewGameMaster = hasModuleViewPermission('GameMaster');

	// const [open, setOpen] = useState(false);
	// const handleOpen = () => setOpen(true);
	// const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	// const [anchorEls, setAnchorEls] = useState<HTMLButtonElement | null>(null);

	const _HeaderPriorityIcon = ({ isCurrMaxPriorityFetched, currMaxPriority }) => {
		const isFetching = useIsFetching();

		if (isFetching) {
			return <CircularProgress color="inherit" size={25} />;
		}
		switch (isCurrMaxPriorityFetched && currMaxPriority?.top_priority) {
			case 'Low':
				return <SvgIcon spriteUrl={lowPriority} className={styles.navIconLow} />;
			case 'Medium':
				return <SvgIcon spriteUrl={mediumPriority} className={styles.navIconMedium} />;
			case 'High':
				return <SvgIcon spriteUrl={highPriority} className={styles.navIconHigh} />;
			case 'Urgent':
				return <SvgIcon spriteUrl={highPriority} className={styles.navIconHigh} />;
			default:
				return null;
		}
	};

	// const { workplaceId } = useWorkplacesContext();
	// const { data: currMaxPriority, isFetched: isCurrMaxPriorityFetched } = useQuery(['top-priority'], () =>
	//     fetchTopPriority(workplaceId)
	// );
	// const auth = useAuth();
	// const { data } = useQuery('notifications', GetNotifications);

	// const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
	// 	setAnchorEl(event.currentTarget);
	// };

	// const handleClicks = (event: any) => {
	// 	setAnchorEls(event.currentTarget);
	// };

	const toggleSidebar = () => {
		// Simply toggle collapsed state
		// If collapsed -> pin it open
		// If pinned open -> close it (but allow hover to expand)
		const newCollapsedState = !collapsed; // false
		setHovered(false);
		setCollapsed(newCollapsedState); // false
		setIsExpanded(!newCollapsedState); // true
		setManualToggle(true);
		onCollapseChange?.(newCollapsedState);
	};

	// Auto-collapse/expand sidebar based on screen width
	useEffect(() => {
		let previousWidth = window.innerWidth;

		const handleResize = () => {
			const currentWidth = window.innerWidth;
			const shouldCollapse = currentWidth < 1200;
			const wasAboveBreakpoint = previousWidth >= 1200;
			const isAboveBreakpoint = currentWidth >= 1200;

			// Reset manual toggle if we cross the breakpoint
			if (wasAboveBreakpoint !== isAboveBreakpoint) {
				setManualToggle(false);
			}

			// Only auto-collapse/expand if user hasn't manually toggled
			if (!manualToggle || wasAboveBreakpoint !== isAboveBreakpoint) {
				setCollapsed(shouldCollapse);
			setIsExpanded(!shouldCollapse);
			setHovered(false);
			onCollapseChange?.(shouldCollapse);
		}

		previousWidth = currentWidth;
	};

		// Add event listener
		window.addEventListener('resize', handleResize);

		// Cleanup
		return () => window.removeEventListener('resize', handleResize);
	}, [manualToggle, onCollapseChange]);

	const handleMouseEnter = () => {
		// Always allow hover expansion when collapsed
		if (collapsed) {
			setHovered(true);
			setIsExpanded(true);
			setHoveredLayout?.(true);
		}
	};

	const handleMouseLeave = () => {
		if (hovered) {
			setHovered(false);
			setIsExpanded(!collapsed);
			setHoveredLayout?.(false);
		}
	};

	const boxRef = useRef(null);

	// Notifications state
	const [notificationsOpen, setNotificationsOpen] = useState(false);
	
	// Fetch notifications to determine unread count
	const { data: apiNotifications = [] } = useQuery(
		'userNotifications',
		fetchUserNotifications,
		{
			refetchInterval: 30000,
			staleTime: 10000,
		}
	);
	
	const notifications = mapAPINotificationsToNotifications(apiNotifications);
	const unreadCount = notifications.filter((n) => !n.isRead).length;
	
	const [_activeItem, setActiveItem] = useState('Dashboard');

	const location = useLocation();

	const isDashboardActive = location.pathname;

	const resetModuleDate = (module: 'checklist' | 'todo' | 'logbook' | 'events') => {
		const today = moment().format('YYYY-MM-DD');
		if (module === 'events') {
			localStorage.setItem('events-selected-date', today);
			localStorage.removeItem('events-calendar-restore-scroll');
			localStorage.removeItem('events-calendar-scroll');
			localStorage.removeItem('events-calendar-scroll-left');
			window.dispatchEvent(new CustomEvent('events-date-changed', { detail: today }));
			return;
		}
		if (module === 'checklist') {
			localStorage.setItem('checklist-selected-date', today);
			return;
		}
		if (module === 'todo') {
			localStorage.setItem('todo-selected-date', today);
			return;
		}
		if (module === 'logbook') {
			localStorage.setItem('logbook-selected-date', today);
		}
	};

	function _isActivePath(locationPathname, activeItem) {
		return locationPathname.toLowerCase().includes(activeItem.toLowerCase()) ? activeItem : null;
	}

	// Close on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (boxRef.current && !boxRef.current.contains(event.target)) {
				setNotificationsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const iconsize = -4;
	return (
		<div
			className={`${styles.asideTop} ${collapsed ? styles.collapsed : ''} ${hovered ? styles.hovered : ''}`}
		>
			<aside
				className={`${styles.mabelSidebar} `}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<nav className={styles.sidebarNav}>
					<div className={styles.sidebarNavTopMenu}>
						<img src={logo} alt="BYOD logo" className={styles.headerLogo} />

						<ul className={styles.sidebarNavTopMenuItemsListData}>
							<li>
								<img src={search} alt="" style={{ width: '16px' }} />
							</li>

							<li style={{ position: 'relative', listStyle: 'none' }} ref={boxRef}>
								<div className={styles.notificationBellWrapper} onClick={() => setNotificationsOpen(!notificationsOpen)}>
								{unreadCount > 0 ? (
									<>
										<img
											src={newbellOutline}
											alt="Notifications"
											className={`${styles.bellIcon} ${styles.bellOutlineIcon}`}
											style={{ width: '16px' }}
										/>
										<img
											src={newbell}
											alt="Notifications"
											className={`${styles.bellIcon} ${styles.bellSolidIcon}`}
											style={{ width: '16px' }}
										/>
									</>
								) : (
									<img
										src={newbellOutline}
										alt="Notifications"
										className={styles.bellIcon}
										style={{ width: '16px' }}
									/>
								)}
									{/* {unreadCount > 0 && (
										<span className={styles.notificationBadge}>
											{unreadCount > 99 ? '99+' : unreadCount}
										</span>
									)} */}
								</div>
								<Notifications
									isOpen={notificationsOpen}
									onClose={() => setNotificationsOpen(false)}
								/>
							</li>
							<li>
								<NavLink
									exact
									to={ROUTES.SETTINGS}
									className={styles.navListLink}
									activeClassName={styles.navListLinkActive}
								>
									<img src={setting} alt="" style={{ width: '16px' }} />
								</NavLink>
							</li>
							<li onClick={toggleSidebar}>
								<img src={drawer} alt="" style={{ width: '16px' }} />
							</li>
						</ul>
					</div>
					<ul className={styles.sidebarMenu}>
						{canViewDashboard && (
							<NavLink
								exact
								to={ROUTES.DASHBOARD}
								className={`${styles.navListItem} ${isDashboardActive === ROUTES.DASHBOARD ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('Dashboard')}
							>
							<li>
								<div className={styles.sidebarIcons}>
									<DashBoardComponent
										size={30 + iconsize}
										color={isDashboardActive === ROUTES.DASHBOARD ? '#fff' : '#000'}
									/>
								</div>

								{isExpanded && (
									<h6
										className={`heading6 ${isDashboardActive === ROUTES.DASHBOARD ? 'sidebar_heading' : ''}`}
									>
										Dashboard
									</h6>
								)}
							</li>
						</NavLink>
					)}
					{isProductAvailable('Scorecard') && canViewScorecard && (
							<NavLink
								exact
								to={ROUTES.SCORECARD}
								className={`${styles.navListItem} ${isDashboardActive === ROUTES.SCORECARD ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('Scorecard')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<MabelScoreCardIcon
											size={30 + iconsize}
											color={isDashboardActive === ROUTES.SCORECARD ? '#fff' : '#000'}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive === ROUTES.SCORECARD ? 'sidebar_heading' : ''}`}
										>
											Scorecard
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Guest Connect') && canViewGuestConnect && (
							<NavLink
								exact
								to={ROUTES.GUESTCONNECT}
								className={`${styles.navListItem} ${isDashboardActive?.includes('/manager/guest-connect') ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('Guest Connect')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<GuestConnect
											size={30 + iconsize}
											color={
												isDashboardActive?.includes('/manager/guest-connect') ? '#fff' : '#000'
											}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive?.includes('/manager/guest-connect') ? 'sidebar_heading' : ''}`}
										>
											Guest Connect
										</h6>
									)}
								</li>
							</NavLink>
						)}{' '}
						{isProductAvailable('Checklist') && canViewChecklist && (
							<NavLink
								exact
								to={ROUTES.CHECKLIST}
								className={`${styles.navListItem} ${isDashboardActive === ROUTES.CHECKLIST ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => {
									resetModuleDate('checklist');
									setActiveItem('Checklist');
								}}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<CheckListComponent
											size={30 + iconsize}
											color={isDashboardActive === ROUTES.CHECKLIST ? '#fff' : '#000'}
										/>
									</div>

									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive === ROUTES.CHECKLIST ? 'sidebar_heading' : ''}`}
										>
											Checklist
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('TODO') && canViewToDo && (
							<NavLink
								exact
								to={ROUTES.TO_DO}
								className={`${styles.navListItem} ${isDashboardActive.startsWith(ROUTES.TO_DO) ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => {
									resetModuleDate('todo');
									setActiveItem('To Do');
								}}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<TodoComponent
											size={30 + iconsize}
											color={isDashboardActive.startsWith(ROUTES.TO_DO) ? '#fff' : '#000'}
										/>
									</div>

									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive.startsWith(ROUTES.TO_DO) ? 'sidebar_heading' : ''}`}
										>
											To-Do
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Logbook') && canViewLogbook && (
							<NavLink
								exact
								to={ROUTES.LOGBOOK}
								className={`${styles.navListItem} ${isDashboardActive.startsWith('/manager/logbook') ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => {
									resetModuleDate('logbook');
									setActiveItem('Logbook');
								}}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<LogbookComponent
											size={30 + iconsize}
											color={isDashboardActive.startsWith('/manager/logbook') ? '#fff' : '#000'}
										/>
									</div>

									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive.startsWith('/manager/logbook') ? 'sidebar_heading' : ''}`}
										>
											Logbook
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Events') && canViewEvents && (
							<NavLink
								exact
								to={ROUTES.EVENTS}
								className={`${styles.navListItem} ${isDashboardActive?.includes(ROUTES.EVENTS) || isDashboardActive?.includes('/customers') ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => {
									resetModuleDate('events');
									setActiveItem('Events');
								}}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<EventCalenderComponent
											size={30 + iconsize}
											color={
												isDashboardActive?.includes(ROUTES.EVENTS) ||
												isDashboardActive?.includes('/customers')
													? '#fff'
													: '#000'
											}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive?.includes(ROUTES.EVENTS) || isDashboardActive?.includes('/customers') ? 'sidebar_heading' : ''}`}
										>
											Events
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Scheduler') && canViewSchedule && (
							<NavLink
								exact
								to={ROUTES.HYPERSCHEDULER}
								className={`${styles.navListItem} ${isDashboardActive === ROUTES.HYPERSCHEDULER ? styles.activeNavListItem : ''}`}
								activeClassName={styles.activeNavListItem}
								onClick={() => setActiveItem('Hyperscheduler')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<IconSchedule
											size={30 + iconsize}
											color={isDashboardActive === ROUTES.HYPERSCHEDULER ? '#fff' : '#000'}
										/>
									</div>

									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive === ROUTES.HYPERSCHEDULER ? 'sidebar_heading' : ''}`}
										>
											Schedule
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Onboarding') && canViewOnboarding && (
							<NavLink
								exact
								to={ROUTES.ONBOARDING}
								className={`${styles.navListItem} ${isDashboardActive.startsWith('/manager/onboarding') ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('Onboarding')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<OnboardingComponent
											size={30 + iconsize}
											color={isDashboardActive.startsWith('/manager/onboarding') ? '#fff' : '#000'}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive.startsWith('/manager/onboarding') ? 'sidebar_heading' : ''}`}
										>
											Onboarding
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{isProductAvailable('Broadcast') && canViewBroadcast && (
							<NavLink
								exact
								to={ROUTES.BROADCAST}
								className={`${styles.navListItem} ${
									isDashboardActive === ROUTES.BROADCAST ||
									isDashboardActive.includes('/broadcast/')
										? styles.activeNavListItem
										: ''
								}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('Broadcast')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<BroadCastComponent
											size={30 + iconsize}
											color={
												isDashboardActive === ROUTES.BROADCAST ||
												isDashboardActive.includes('/broadcast/')
													? '#fff'
													: '#000'
											}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${
												isDashboardActive === ROUTES.BROADCAST ||
												isDashboardActive.includes('/broadcast/')
													? 'sidebar_heading'
													: ''
											}`}
										>
											Broadcast
										</h6>
									)}
								</li>
							</NavLink>
						)}
						{/* <NavLink
                            exact
                            to={ROUTES.LABORSABER}
                            className={isDashboardActive === ROUTES.LABORSABER ? styles.activeNavListItem : styles.navListItem}
                            activeClassName={styles.navListLinkActive}
                            onClick={() => setActiveItem('Laborsaber')}
                        >
                            <li>
                                <Home
                                    color="primary"
                                    fontSize="medium"
                                    className={isDashboardActive === ROUTES.LABORSABER ? 'sidebar_heading' : ''}
                                />
                                {isExpanded && (
                                    <h6 className={`heading6 ${isDashboardActive === ROUTES.LABORSABER ? 'sidebar_heading' : ''}`}>
                                        Laborsaber
                                    </h6>
                                )}
                            </li>
                        </NavLink> */}
						{isProductAvailable('Game Master') && canViewGameMaster && (
							<NavLink
								exact
								to={ROUTES.GAMEMASTER}
								className={`${styles.navListItem} ${isDashboardActive === ROUTES.GAMEMASTER ? styles.activeNavListItem : ''}`}
								activeClassName={styles.navListLinkActive}
								onClick={() => setActiveItem('GameMaster')}
							>
								<li>
									<div className={styles.sidebarIcons}>
										<GameMasterComponent
											size={30 + iconsize}
											color={isDashboardActive === ROUTES.GAMEMASTER ? '#fff' : '#000'}
										/>
									</div>
									{isExpanded && (
										<h6
											className={`heading6 ${isDashboardActive === ROUTES.GAMEMASTER ? 'sidebar_heading' : ''}`}
										>
											GameMaster
										</h6>
									)}
								</li>
							</NavLink>
						)}
					</ul>
				</nav>
				<div className={styles.sidebarFooter}>
					<div className={styles.helpSupport}>
						<ChatBubble color="primary" fontSize="small" />
						{isExpanded && (
							<a href="mailto:support@byod.ai">
								<h6 className="heading6">Help & Support</h6>
							</a>
						)}
					</div>
					{isExpanded && (
						<div className={styles.helpSupportPrivacy}>
							<a href="https://byod.ai/privacy-policy/" target="_blank" rel="noreferrer">
								Privacy Policy
							</a>
							<a href="https://byod.ai/acceptable-use-policy/" target="_blank" rel="noreferrer">
								Terms of Use
							</a>
						</div>
					)}
					{isExpanded && (
						<div className={styles.copyrightText}>
							© {new Date().getFullYear()} BYOD, Inc. All rights reserved.
						</div>
					)}
				</div>
			</aside>
		</div>
	);
};

export default MabelSidebar;
